/**
 * Глобальная обработка «сессия завершена» (HTTP 401).
 *
 * Зачем: когда админ нажимает «Сменить пароль и сбросить сессию» в
 * AdminUserPage.vue, бэк помечает revoked все oauth_access_tokens
 * пользователя. Но Bearer-токен живёт в localStorage клиента —
 * физически выкинуть SPA можно только при следующем HTTP-запросе. Чтобы
 * не разводить обработку 401 по всем `jsonFetch`'ам (их у нас 5+ копий),
 * вынесли общий helper.
 *
 * Контракт:
 *   - `handleUnauthorized()` идемпотентен — несколько одновременных запросов,
 *     получивших 401, не вызовут несколько редиректов.
 *   - Чистим всю авторизацию (token, userInfo, chat_session_token), рвём
 *     WebSocket (Echo держит подписки на старом токене), а затем редиректим
 *     на /login c `?redirect=` чтобы после логина вернуть юзера на ту же
 *     страницу.
 *   - На странице /login и других unauthorizedOnly роутах ничего не делаем
 *     (юзер уже там, дёргать редирект бессмысленно).
 *
 * Используется обёрткой `jsonFetchWithAuth()` ниже — все админские/чат-
 * клиенты (`adminChatApi`, `chatApi`, `supportApi`, `adminPaymentsApi`,
 * `adminSupportApi`, `adminPublicReportsApi`) переходят на неё, чтобы
 * 401 вышибал юзера в реальном времени.
 */

import store from '@/store'
import router from '@/router'
import { destroyEcho } from '@/utils/echo'

let handling = false
let heartbeatTimer = null

/**
 * Глобальный fetch-interceptor.
 *
 * Зачем: на странице юзера в админке есть кнопка «Сменить пароль и сбросить
 * сессию». Бэк revoked-ит все oauth_access_tokens пользователя, и любой
 * следующий запрос с протухшим Bearer-токеном получает 401. Хочется, чтобы
 * SPA на это реагировала автоматически — независимо от того, идёт ли запрос
 * через `fetchJsonWithAuth()` или через старый `fetch()` в каком-нибудь
 * компоненте/`requests.js`.
 *
 * Решение: один раз при загрузке приложения патчим глобальный `window.fetch`
 * так, чтобы каждый ответ 401 триггерил `handleUnauthorized()`. Сам объект
 * Response не модифицируется — вызывающий код продолжает получать тот же
 * Response/Error, что и раньше. Это идемпотентно (повторный installFetch
 * Interceptor — no-op).
 *
 * Также запускаем heartbeat: раз в 15 сек тихо дёргаем `GET /api/users/details`.
 * Если на вкладке юзер ничего не делает — без heartbeat'а его не выкинет до
 * следующего действия. С ним — выкинет в течение 15 сек после revoke.
 */
const FETCH_INTERCEPTOR_FLAG = '__amAuthFetchPatched'
let nativeFetch = null

function shouldHandle401(url) {
    if (!url) return true
    let path = ''
    try {
        path = typeof url === 'string'
            ? new URL(url, window.location.origin).pathname
            : new URL(url.url, window.location.origin).pathname
    } catch {
        path = String(url)
    }
    // Запросы на сам логин/восстановление пароля / regis — там 401 это
    // нормальный ответ «неверные креды», выкидывать с него не нужно.
    if (path.endsWith('/api/login')) return false
    if (path.endsWith('/api/logout')) return false
    if (path.includes('/api/reset-password')) return false
    if (path.includes('/api/forgot-password')) return false
    if (path.includes('/api/oauth/token')) return false
    return true
}

export function installFetchInterceptor() {
    if (typeof window === 'undefined') return
    if (window[FETCH_INTERCEPTOR_FLAG]) return
    if (typeof window.fetch !== 'function') return

    nativeFetch = window.fetch.bind(window)
    window.fetch = async function patchedFetch(input, init) {
        const response = await nativeFetch(input, init)
        try {
            if (response && response.status === 401) {
                const url = typeof input === 'string' ? input : (input?.url || '')
                if (shouldHandle401(url)) {
                    handleUnauthorized()
                }
            }
        } catch { /* noop */ }
        return response
    }
    window[FETCH_INTERCEPTOR_FLAG] = true
}

/**
 * Лёгкий «пинг» — раз в N сек тихо проверяем, что Bearer-токен ещё жив.
 * Любой 401 пройдёт через installFetchInterceptor() выше и автоматически
 * вызовет handleUnauthorized(). Запускаем только при наличии токена;
 * при logout — останавливаем сами.
 */
const HEARTBEAT_INTERVAL_MS = 15_000

export function startAuthHeartbeat() {
    if (typeof window === 'undefined') return
    if (heartbeatTimer) return
    const base = process.env.VUE_APP_URL_DEV || ''
    const tick = async () => {
        const token = localStorage.getItem('token')
        if (!token) return
        try {
            await fetch(`${base}/api/users/details`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                // Отключаем кеш браузера — нам важно реальное состояние
                // токена на каждом тике, иначе 304 не даст узнать о 401.
                cache: 'no-store',
            })
        } catch { /* сеть моргнула — ничего не делаем */ }
    }
    heartbeatTimer = setInterval(tick, HEARTBEAT_INTERVAL_MS)
}

export function stopAuthHeartbeat() {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer)
        heartbeatTimer = null
    }
}

/**
 * Запускает выход + редирект на /login. Безопасно вызывать многократно —
 * фактически работа делается один раз.
 */
export function handleUnauthorized() {
    if (handling) return
    handling = true

    try {
        // Чистый logout: убираем token/userInfo, гостевой session_token и
        // рвём WS, чтобы Echo не пытался переподключаться на отозванном
        // токене.
        store.dispatch('main/logout')
    } catch {
        // store может ещё не быть готов — на всякий случай зачищаем руками
        try { localStorage.removeItem('token') } catch { /* noop */ }
        try { localStorage.removeItem('userInfo') } catch { /* noop */ }
        try { localStorage.removeItem('chat_session_token') } catch { /* noop */ }
        try { destroyEcho() } catch { /* noop */ }
    }

    // Останавливаем heartbeat — следующий тик пытался бы ходить с уже
    // удалённым токеном и снова дёргать handleUnauthorized() в цикле.
    try { stopAuthHeartbeat() } catch { /* noop */ }

    try {
        const current = router?.currentRoute?.value
        // На страницах логина/регистрации редиректить незачем.
        if (current && current.meta?.unauthorizedOnly) {
            handling = false
            return
        }
        // ВАЖНО: используем именно window.location.assign, а не router.push.
        //
        // У нас в SPA несколько «независимых» источников состояния
        // авторизации (Vuex state.userInfo, локальные ref в
        // useChatAccessMock, кэш `ChatSidebarUser.props.name`). После
        // мягкого `router.push('/login')` Vuex чистится, но локальные
        // composable'ы про это не узнают, и в верхнем сайдбаре имя юзера
        // остаётся висеть до перезагрузки. Жёсткая полная перезагрузка
        // страницы гарантирует, что все ref'ы пересоздадутся с чистого
        // листа — это то поведение, которое ожидается после «выкинуло из
        // сессии».
        const currentPath = (typeof window !== 'undefined' && window.location?.pathname) || '/'
        const search = (typeof window !== 'undefined' && window.location?.search) || ''
        const fullPath = currentPath + search
        const target = fullPath && fullPath !== '/login'
            ? `/login?redirect=${encodeURIComponent(fullPath)}`
            : '/login'
        if (typeof window !== 'undefined' && window.location?.pathname !== '/login') {
            window.location.assign(target)
        }
    } catch {
        if (typeof window !== 'undefined' && window.location?.pathname !== '/login') {
            try { window.location.assign('/login') } catch { /* noop */ }
        }
    }

    // Разрешаем повторную обработку: после logout/redirect Vuex-state
    // обновится, и любые «опоздавшие» 401-ответы пройдут мимо безболезненно.
    // Сбрасываем флаг чуть позже, чтобы все они «слиплись» в один редирект.
    setTimeout(() => { handling = false }, 1000)
}

/**
 * Универсальная обёртка над `fetch`, которая:
 *   1) дёргает 401 → handleUnauthorized() (один редирект на всех)
 *   2) парсит JSON и кидает Error со status/payload как раньше
 *   3) ничего не знает про headers — их должен подмешать вызывающий
 *      (`authHeaders()` в каждом *Api.js, потому что есть нюансы:
 *      X-Chat-Session-Token, Accept и т.д.).
 *
 * Контракт по ошибкам совпадает с прежними `jsonFetch()`:
 *   throw new Error(message); err.status, err.payload.
 */
export async function fetchJsonWithAuth(url, options = {}, onResponse = null) {
    const response = await fetch(url, options)

    if (typeof onResponse === 'function') {
        try { onResponse(response) } catch { /* noop */ }
    }

    const data = await response.json().catch(() => ({}))

    if (response.status === 401) {
        // На 401 — выкидываем юзера на /login и кидаем ошибку, чтобы
        // вызывающий код не пытался отрисовать «полу-данные».
        handleUnauthorized()
        const err = new Error(data?.message || 'Unauthorized')
        err.status = 401
        err.payload = data
        throw err
    }

    if (!response.ok) {
        const err = new Error(data?.message || `Request failed: ${response.status}`)
        err.status = response.status
        err.payload = data
        throw err
    }

    return data
}
